import type { MaybeRefOrGetter } from 'vue';
import type { ValueOf } from '#layers/base/public';
import type { Filter, GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, QuerySchemaFilter, ResolvedMonospaceCollection, SegmentedFieldFilter } from '#layers/data-model/public';
import type { FilterSchemaLookup } from '#layers/filters/components/Filter/utils/filter-path';
import type { FilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import type { ClauseRules } from '#layers/filters/components/Filter/utils/is-clause-allowed';
import { createContext } from 'reka-ui';
import { toValue } from 'vue';
import { QueryCapabilityKinds, supportedSurface, unwrapSingleTermGroups, useQueryApi } from '#layers/data-model/public';
import { serializeSegmentedFilter } from '#layers/filters/components/Filter/utils/filter-path';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { isClauseAllowed } from '#layers/filters/components/Filter/utils/is-clause-allowed';

/**
 * Which filters a filter UI may offer: `full` follows the collection's read filter and the user's read
 * permissions (item queries), `schema` only the read filter (policy rules, evaluated for other users),
 * `none` offers everything (filters over data outside the workspace schema).
 */
export const FilterGateMode = {
	Full: 'full',
	Schema: 'schema',
	None: 'none',
} as const;

export type FilterGateMode = ValueOf<typeof FilterGateMode>;

type GatedCollection = MaybeRefOrGetter<Pick<ResolvedMonospaceCollection, 'id' | 'isSystem'> | null | undefined>;

export type FilterGateOptions =
	| { mode: typeof FilterGateMode.Full; collection: GatedCollection; canReadField: (collectionId: GlobalCollectionId, fieldId: GlobalFieldId) => boolean }
	| { mode: typeof FilterGateMode.Schema; collection: GatedCollection }
	| { mode: typeof FilterGateMode.None };

/**
 * A filter control asks with the clause it would emit, built by the helper that emits it, so the
 * check and the filter cannot drift apart. Pickers ask with a path before anything is emitted.
 */
export interface FilterGate {
	/** Whether this gate restricts anything; `false` for ungated owners and system collections. */
	isGated: () => boolean;
	/** Whether a picker may offer a path of field ids: every field exists in the read filter and is readable. */
	allowsPath: (path: readonly GlobalFieldId[]) => boolean;
	/** Whether the read filter accepts a clause over the owner's collection, as a control would emit it. */
	allowsClause: (clause: Filter) => boolean;
}

const UNGATED: FilterGate = {
	isGated: () => false,
	allowsPath: () => true,
	allowsClause: () => true,
};

const [injectFilterGate, provideFilterGateContext] = createContext<FilterGate>('FilterGate');

/** Whether the gate accepts the clause a segmented filter row serializes to; an incomplete row is not. */
export function allowsSegmentedFilter(gate: FilterGate, schema: FilterSchemaLookup, filter: SegmentedFieldFilter): boolean {
	if (!gate.isGated()) return true;

	try {
		return gate.allowsClause(serializeSegmentedFilter(filter, schema));
	}
	catch {
		return false;
	}
}

/** Throws outside a filter owner: every owner must decide how its filters are gated. */
export function useFilterGate(): FilterGate {
	return injectFilterGate();
}

/**
 * Every filter owner states how its filters are gated; descendants read it through `useFilterGate`.
 * Pass `schemaContext` when the same component provides it, since a component cannot inject what it provides.
 */
export function provideFilterGate(options: FilterGateOptions, schemaContext?: FilterSchemaContext): FilterGate {
	const gate = options.mode === FilterGateMode.None ? UNGATED : createFilterGate(options, schemaContext ?? injectFilterSchemaContext());
	provideFilterGateContext(gate);
	return gate;
}

function createFilterGate(options: Exclude<FilterGateOptions, { mode: typeof FilterGateMode.None }>, schema: FilterSchemaContext): FilterGate {
	const filterInput = useQueryApi(options.collection).surface((api) => api.readMany()?.filter());

	function readable(id: GlobalFieldId) {
		if (options.mode === FilterGateMode.Schema) return true;
		const field = schema.findFieldById(id);
		return !!field?.collectionId && options.canReadField(field.collectionId, field.id);
	}

	/** The read filter surface a path of relation ids leads into; `undefined` when it leaves the read filter. */
	function surfaceAt(path: readonly GlobalFieldId[]): QuerySchemaFilter | undefined {
		let surface = supportedSurface(filterInput.value);

		for (const id of path) {
			if (!surface || !readable(id)) return;
			surface = surface.relationFieldById(id as GlobalSingleRelationFieldId);
		}

		return surface;
	}

	/** The surface holding a path's last field, when that field is readable. */
	function parentOf(path: readonly GlobalFieldId[]) {
		const id = path.at(-1);
		if (id === undefined || !readable(id)) return;
		const surface = surfaceAt(path.slice(0, -1));
		return surface && { surface, id };
	}

	const rules: ClauseRules = {
		allowsPath: (path) => {
			const parent = parentOf(path);
			return !!parent && !!(parent.surface.primitiveFieldById(parent.id as GlobalPrimitiveFieldId) ?? parent.surface.relationFieldById(parent.id as GlobalSingleRelationFieldId));
		},
		allowsOperator: (path, operator) => {
			const parent = parentOf(path);
			return !!parent?.surface.primitiveFieldById(parent.id as GlobalPrimitiveFieldId)?.operators().includes(operator);
		},
		allowsQuantifier: (path, quantifier) => {
			const parent = parentOf(path);
			return !!parent?.surface.relationQuantifiersById(parent.id as GlobalSingleRelationFieldId).includes(quantifier);
		},
		allowsLogical: (operator, path) => !!surfaceAt(path)?.logical(operator),
		allowsSeveralKeys: (path) => !!surfaceAt(path)?.combinesKeys(),
		allowsSeveralOperators: (path) => {
			const parent = parentOf(path);
			return !!parent?.surface.primitiveFieldById(parent.id as GlobalPrimitiveFieldId)?.combinesOperators();
		},
	};

	const ungated = () => filterInput.value.kind === QueryCapabilityKinds.Ungated;

	return {
		isGated: () => !ungated(),
		allowsPath: (path) => ungated() || rules.allowsPath(path),
		allowsClause: (clause) => {
			if (ungated()) return true;
			const root = toValueCollection();
			// Judge the clause as the request sends it, with one-term groups unwrapped.
			return !!root && isClauseAllowed(rules, schema, root, unwrapSingleTermGroups(clause));
		},
	};

	function toValueCollection() {
		const collection = toValue(options.collection);
		return collection ? schema.findCollectionById(collection.id) : undefined;
	}
}
